import {gql} from '@apollo/client'
import {GraphQLFormattedError} from 'graphql'
import React, {
  MouseEventHandler,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import {createPortal} from 'react-dom'
import styled from 'styled-components'
import SVGClose from '../assets/icons/close.svg'
import {
  default as SVGCommentHeader,
  default as SVGCommentInline
} from '../assets/icons/comment-icon-inline.svg'
import SVGRemoveSmall from '../assets/icons/remove-small.svg'
import SVGSendSmall from '../assets/icons/send-small.svg'

import {
  EntitySelector,
  EntitySelectorCodec
} from '../common/types/workbench-comment'
import {BetaFlagMarker} from './feature-flags'
import {Tooltip} from './library/tooltip/tooltip'
import {Loader} from './loader'
import {Flags, useFlags} from '../hooks/use-flags'
import {ENVIRONMENT_NAME} from '../lib/env'
import {
  CommentModalContext,
  useCommentModalOpen,
  useCommentMode
} from '../providers/comments'
import {fontSmall} from '../views/action-flow/components/library/css'
import {Input} from '../views/action-flow/components/library/input'
import {
  useAddCommentMutation,
  useCommentsForEntityQuery,
  useDeleteCommentMutation,
  useUpdateCommentMutation
} from './generated/shared-comment'

gql`
  mutation addComment(
    $organisationId: String!
    $environmentId: String!
    $entitySelector: JSON!
    $entityId: String!
    $comment: String!
  ) {
    createWorkbenchComment(
      input: {
        workbenchComment: {
          organisationId: $organisationId
          environmentId: $environmentId
          entitySelector: $entitySelector
          entityId: $entityId
          entityType: ACTION_FLOW_CONFIG_VERSION
          value: $comment
        }
      }
    ) {
      workbenchComment {
        id
        value
      }
    }
  }

  mutation updateComment($id: String!, $value: String!) {
    updateWorkbenchComment(input: {id: $id, patch: {value: $value}}) {
      workbenchComment {
        id
        value
      }
    }
  }

  query commentsForEntity($environmentId: String!, $entityId: String!) {
    workbenchComments(
      condition: {
        entityType: ACTION_FLOW_CONFIG_VERSION
        environmentId: $environmentId
        entityId: $entityId
      }
    ) {
      nodes {
        id
        value
        entitySelector
        entityId
        nodeId
      }
    }
  }

  mutation deleteComment($id: String!) {
    deleteWorkbenchComment(input: {id: $id}) {
      workbenchComment {
        id
      }
    }
  }
`

const useWorkbenchComment = ({
  entitySelector,
  entityId,
  environmentId,
  organisationId,
  skip
}: {
  entitySelector: string
  entityId: string
  environmentId: string
  organisationId: string
  skip?: boolean
}) => {
  const [errors, setErrors] = useState<
    Readonly<GraphQLFormattedError[]> | undefined
  >(undefined)
  // the server state of the comment
  const [draftComment, setDraftComment] = useState('')

  // reset the comment when the entity changes
  useEffect(() => {
    setDraftComment('')
  }, [entityId, entitySelector, environmentId, organisationId])

  const [addCommentMutation, {loading: adding}] = useAddCommentMutation({
    onError: e => setErrors(e.graphQLErrors),
    onCompleted: () => setErrors(undefined)
  })
  const [updateCommentMutation, {loading: updating}] = useUpdateCommentMutation(
    {
      onError: e => setErrors(e.graphQLErrors),
      onCompleted: () => setErrors(undefined)
    }
  )
  const [deleteCommentMutation, {loading: deleting}] =
    useDeleteCommentMutation()

  const {
    data: entityComments,
    refetch,
    loading
  } = useCommentsForEntityQuery({
    variables: {
      entityId,
      environmentId
    },
    skip,
    onError: e => setErrors(e.graphQLErrors),
    onCompleted: () => setErrors(undefined)
  })

  const comment = useMemo(() => {
    const foundComment = entityComments?.workbenchComments?.nodes.filter(
      c =>
        EntitySelectorCodec.is(c.entitySelector) &&
        entitySelectorsEqual(
          c.entitySelector,
          makeEntitySelector(entitySelector)
        )
    )
    if (foundComment?.length ?? 0 > 1) {
      console.error(
        'Found multiple comments for entity',
        entityId,
        entitySelector
      )
    }
    return foundComment?.[0]
  }, [entityComments, entitySelector, entityId])

  // reset the tmp comment when the comment changes
  useEffect(() => {
    setDraftComment(comment?.value ?? '')
  }, [comment])

  const saveComment = useCallback(() => {
    if (!draftComment) return
    if (comment) {
      updateCommentMutation({
        variables: {
          id: comment.id,
          value: draftComment
        }
      }).then(() => refetch())
    } else {
      addCommentMutation({
        variables: {
          organisationId,
          environmentId,
          entitySelector: makeEntitySelector(entitySelector),
          entityId,
          comment: draftComment
        }
      }).then(() => refetch())
    }
  }, [
    draftComment,
    comment,
    updateCommentMutation,
    addCommentMutation,
    organisationId,
    environmentId,
    entitySelector,
    entityId,
    refetch
  ])

  const deleteComment = useCallback(() => {
    if (!comment) return
    deleteCommentMutation({
      variables: {
        id: comment.id
      }
    }).then(() => refetch())
  }, [comment, deleteCommentMutation, refetch])

  return {
    setDraftComment,
    draftComment,
    saveComment,
    savedComment: comment,
    isSaved: !!comment,
    isDirty: !(draftComment === comment?.value || (!draftComment && !comment)),
    loading: loading || adding || updating || deleting,
    deleteComment,
    error: errors?.map(e => e.message)?.join(', ') // could be better?
  }
}

const ModalWrapper = styled.div`
  position: relative;
  border-radius: ${({theme}) => theme.borderRadius};
  box-shadow: ${({theme}) => theme.shadowDepth3};
  z-index: 999;
  // position to the bottom left of trigger
  transform: translate(calc(-100% - 10px), 15px);
  pointer-events: all;
`

const CommentSection = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 400px;
  gap: 4px;
  padding: 6px;
  background: ${props => props.theme.colors.baseWhite};
  border-radius: 4px;
  color: ${props => props.theme.colors.grey11};
  ${fontSmall}
`

const CommentInlineIconWrapper = styled.div<{hasMessage: boolean}>`
  display: flex;
  align-items: center;
  padding: 4px;
  height: 28px;
  width: 28px;
  border-radius: 4px;
  cursor: pointer;
  background: ${({theme, hasMessage}) =>
    hasMessage ? theme.colors.green3 : 'transparent'};
  svg {
    color: ${({theme, hasMessage}) =>
      hasMessage ? theme.colors.green10 : theme.colors.grey10};
  }
`

const CommentIcon = ({
  hasMessage,
  onClick
}: {
  hasMessage: boolean
  onClick: MouseEventHandler<HTMLDivElement>
}) => {
  return (
    <CommentInlineIconWrapper hasMessage={hasMessage} onClick={onClick}>
      <SVGCommentInline />
    </CommentInlineIconWrapper>
  )
}

const CommentToggleIconWrapper = styled.button<{enabled: boolean}>`
  border: none;
  display: flex;
  align-items: center;
  padding: 4px;
  height: 28px;
  width: 28px;
  border-radius: 4px;
  :hover {
    background: ${({theme, enabled}) =>
      enabled ? theme.colors.green10 : 'none'};
  }
  background: ${p => (p.enabled ? p.theme.colors.green9 : 'none')};
  svg {
    color: ${({theme, enabled}) =>
      enabled ? theme.colors.baseWhite : theme.colors.grey10};
  }
`

/** Turn on/off comments for the whole workbench */
export const CommentModeToggle = () => {
  const {setCommentModeEnabled, commentModeEnabled} =
    useContext(CommentModalContext)!

  return (
    <Tooltip
      content={`${commentModeEnabled ? 'Hide' : 'Show'} comment mode`}
      position="bottom-right"
      offsetBottom="4px"
      delayMs={1000}
    >
      <CommentToggleIconWrapper
        enabled={commentModeEnabled}
        onClick={() => setCommentModeEnabled(c => !c)}
      >
        <SVGCommentHeader />
      </CommentToggleIconWrapper>
    </Tooltip>
  )
}

export const ElementInternalComment = ({
  organisationId,
  environmentId,
  entitySelector,
  actionFlowConfigVersionId
}: {
  environmentId: string
  entitySelector: string
  actionFlowConfigVersionId: string
  organisationId: string
}) => {
  const flags = useFlags(organisationId)

  const entityId = actionFlowConfigVersionId

  // don't want to make server requests in non-comment environments
  const isCommentEnv = isCommentsEnabledEnv(flags)

  const {
    setDraftComment,
    draftComment,
    saveComment,
    loading,
    deleteComment,
    isSaved,
    isDirty,
    savedComment
  } = useWorkbenchComment({
    entitySelector,
    entityId,
    environmentId,
    organisationId,
    skip: !isCommentEnv
  })

  const elementRef = useRef<HTMLDivElement>(null)

  const triggerRect: DOMRect | undefined =
    elementRef.current?.getBoundingClientRect()

  const handleDelete = useCallback(() => {
    if (window.confirm('Are you sure you want to delete this comment?')) {
      deleteComment()
    }
  }, [deleteComment])

  const {isOpen, setOpen} = useCommentModalOpen(false)
  const {commentModeEnabled} = useCommentMode()

  if (!isCommentEnv || !commentModeEnabled) return null

  return (
    <div ref={elementRef}>
      <CommentIcon hasMessage={!!savedComment} onClick={() => setOpen(true)} />
      {isOpen
        ? createPortal(
            <div
              style={{
                position: 'fixed',
                zIndex: 99,
                top: triggerRect?.top,
                left: triggerRect?.left,
                pointerEvents: 'none'
              }}
            >
              <ModalWrapper>
                <CommentSection>
                  <div style={{display: 'flex', alignContent: 'space-between'}}>
                    <BetaFlagMarker />
                    <SVGClose
                      width={16}
                      height={16}
                      style={{marginLeft: 'auto', cursor: 'pointer'}}
                      onClick={() => setOpen(false)}
                    />
                  </div>
                  <Input
                    label={`Shared comment ${isDirty ? '(unsaved)' : ''}`}
                    value={draftComment}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        saveComment()
                      }
                    }}
                    rightIcon={
                      <div style={{display: 'flex', gap: '4px'}}>
                        {loading ? (
                          <Loader size={18} style={{padding: 'unset'}} />
                        ) : (
                          <SVGSendSmall
                            width="16"
                            height="16"
                            onClick={() => saveComment()}
                            style={{
                              cursor:
                                draftComment.length > 0
                                  ? 'pointer'
                                  : 'not-allowed'
                            }}
                          />
                        )}
                        {isSaved && (
                          <SVGRemoveSmall
                            width="16"
                            height="16"
                            style={{cursor: 'pointer'}}
                            onClick={() => handleDelete()}
                          />
                        )}
                      </div>
                    }
                    onChange={newValue => setDraftComment(newValue)}
                    disabled={loading}
                  />
                </CommentSection>
              </ModalWrapper>
            </div>,
            document.body
          )
        : null}
    </div>
  )
}

const makeEntitySelector = (entityId: string): EntitySelector =>
  ({
    type: 'af-path',
    value: entityId
  }) as const

const entitySelectorsEqual = (es1: EntitySelector, es2: EntitySelector) =>
  es1.type === es2.type && es1.value === es2.value

/** Do we want to show comments in this env */
export const isCommentsEnabledEnv = (flags: Flags) => {
  return (
    flags.isInternal &&
    (ENVIRONMENT_NAME === 'development' || ENVIRONMENT_NAME === 'local')
  )
}
